// Rectangle shape strategy

import { registerShape } from '../services/ShapeRegistry';
import { findEdgeSideIntersection } from '../utils/geometryUtils';
import type { LineSeg } from '../utils/geometryUtils';
import type {
  ShapeDefinition,
  ShapeDrawSettings,
  EdgeData,
  Point,
  CollisionShape,
} from '../types/ShapeTypes';

function drawPath(settings: ShapeDrawSettings): void {
  const { ctx, node, center: { x, y }, border, zoomLevel } = settings;
  const width = border ? node.width + node.borderWidth : node.width + node.borderWidth * 2;
  const height = border ? node.height + node.borderWidth : node.height + node.borderWidth * 2;
  const computedWidth = node.border ? width : node.width;
  const computedHeight = node.border ? height : node.height;
  const computedX = x - (computedWidth / 2) * zoomLevel;
  const computedY = y - (computedHeight / 2) * zoomLevel;
  ctx.rect(computedX, computedY, computedWidth * zoomLevel, computedHeight * zoomLevel);
}

function getRectSides(
  rectX: number,
  rectY: number,
  rectWidth: number,
  rectHeight: number
): LineSeg[] {
  const top: LineSeg = { x3: rectX, y3: rectY, x4: rectX + rectWidth, y4: rectY };
  const right: LineSeg = { x3: rectX + rectWidth, y3: rectY, x4: rectX + rectWidth, y4: rectY + rectHeight };
  const bottom: LineSeg = { x3: rectX + rectWidth, y3: rectY + rectHeight, x4: rectX, y4: rectY + rectHeight };
  const left: LineSeg = { x3: rectX, y3: rectY + rectHeight, x4: rectX, y4: rectY };
  return [top, right, bottom, left];
}

function getEdgeConnectionPoint(edge: EdgeData, connected: 'from' | 'to'): Point {
  const line1 = { x1: edge.startPosX, y1: edge.startPosY, x2: edge.endPosX, y2: edge.endPosY };

  const isFrom = connected === 'from';
  const rectWidth = isFrom
    ? edge.fromNodeWidth + edge.fromNodeBorderWidthValue * 2
    : edge.toNodeWidth + edge.toNodeBorderWidthValue * 2;
  const rectHeight = isFrom
    ? edge.fromNodeHeight + edge.fromNodeBorderWidthValue * 2
    : edge.toNodeHeight + edge.toNodeBorderWidthValue * 2;
  const rectX = isFrom
    ? edge.startPosX - edge.fromNodeWidth / 2 - edge.fromNodeBorderWidthValue
    : edge.endPosX - edge.toNodeWidth / 2 - edge.toNodeBorderWidthValue;
  const rectY = isFrom
    ? edge.startPosY - edge.fromNodeHeight / 2 - edge.fromNodeBorderWidthValue
    : edge.endPosY - edge.toNodeHeight / 2 - edge.toNodeBorderWidthValue;

  const sides = getRectSides(rectX, rectY, rectWidth, rectHeight);
  return findEdgeSideIntersection(line1, sides);
}

function getCollisionData(edge: EdgeData, side: 'from' | 'to'): CollisionShape {
  const isFrom = side === 'from';
  const x = isFrom
    ? edge.startPosX - edge.fromNodeWidth / 2 - edge.fromNodeBorderWidthValue
    : edge.endPosX - edge.toNodeWidth / 2 - edge.toNodeBorderWidthValue;
  const y = isFrom
    ? edge.startPosY - edge.fromNodeHeight / 2 - edge.fromNodeBorderWidthValue
    : edge.endPosY - edge.toNodeHeight / 2 - edge.toNodeBorderWidthValue;
  const width = isFrom
    ? edge.fromNodeWidth + edge.fromNodeBorderWidthValue * 2
    : edge.toNodeWidth + edge.toNodeBorderWidthValue * 2;
  const height = isFrom
    ? edge.fromNodeHeight + edge.fromNodeBorderWidthValue * 2
    : edge.toNodeHeight + edge.toNodeBorderWidthValue * 2;

  return { type: 'rect', data: { x, y, width, height } };
}

const rectangleShape: ShapeDefinition = {
  value: 'rectangle',
  drawPath,
  getEdgeConnectionPoint,
  getCollisionData,
  resize: { constrainDimensions: false },
};

registerShape(rectangleShape);

export { rectangleShape };
